import {
  DOCUMENT_KINDS,
  DOCUMENT_SCOPES,
  type DocumentKind,
  type DocumentScope,
  documentInputSchema,
  KINDS_BY_SECTION,
  revisionInputSchema,
  uploadFileProblem,
  vesselDocumentInputSchema,
} from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Drawer,
  Group,
  MultiSelect,
  Progress,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconFileUpload,
  IconInfoCircle,
  IconLock,
  IconPaperclip,
  IconX,
} from '@tabler/icons-react';
import { type DragEvent, useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { ApprovalChainEditor } from './ApprovalChainEditor.js';
import {
  type ChainDraft,
  type ChainRole,
  chainDraftProblem,
  chainToDraft,
} from './approval-chain.js';
import classes from './Documents.module.css';
import {
  documentHeading,
  folderLabel,
  formatFileSize,
  nextRevisionNo,
  revisionChoices,
} from './library.js';
import { largeFileHours } from './office.js';
import {
  createSaveState,
  fileKey,
  lockedAttachments,
  lockedFields,
  type SaveState,
  saveRevisionDraft,
  saveShipRevisionDraft,
  scopeChanged,
} from './revision-save.js';
import type {
  DocumentVessel,
  OfficeDocumentActions,
  PickedFile,
  ShipLibraryActions,
} from './source.js';
import type {
  LibraryDetail,
  LibraryFolder,
  LibraryRevision,
  LibrarySection,
  LibraryTree,
} from './types.js';
import { writableFolders } from './vessel-library.js';

export type RevisionFormMode = 'new' | 'revision';

/** Tarayıcının dosya penceresinin süzgeci: kabul edilen türler (ADR 0055 §2). */
const UPLOAD_ACCEPT = '.docx,.xlsx,.pdf,.jpg,.jpeg,.png';

/** Ana dosya Word ya da Excel mi (baskı PDF'i yalnız onlarda; ADR 0028 #34(1)). */
const isOfficeName = (name: string) => /\.(docx|xlsx)$/i.test(name);

/**
 * Formdaki dosya: web ofiste tarayıcının `File`'ı, gemide ana sürecin seçtiği dosya (`PickedFile`;
 * yol arayüze gelmez, bayt IPC'den geçmez; ADR 0038 §13).
 */
export type FormFile = File | PickedFile;

/** Form ofiste şirket belgesi (kapsamlı), gemide geminin kendi belgesi (kapsam örtük; §12). */
export type RevisionFormVariant = 'office' | 'ship';

export interface RevisionFormValues {
  folderId: string;
  docNo: string;
  title: string;
  kind: DocumentKind;
  description: string;
  scope: DocumentScope;
  scopeVesselIds: string[];
  revisionNo: string;
  changeSummary: string;
  file: FormFile | null;
  attachments: FormFile[];
  /**
   * Word ya da Excel ana dosyanın aslına uygun baskı PDF'i (isteğe bağlı; ADR 0028 #34(1), ADR
   * 0055 §5): Print ve Print Form onu basar, önizleme onu gösterir.
   */
  printCopy: FormFile | null;
  /**
   * Edit'te kapsam değişikliğinin yürürlükteki revizyona hemen uygulanacağı kabul edildi
   * (inceleme M8a3-R2; ADR 0035 Ek).
   */
  scopeConfirmed: boolean;
  /**
   * Formun onay zinciri (ADR 0071 §2): yalnız ofiste ve `form` türünde. Verilmezse hazır kural
   * (onay matrisi).
   */
  approvalChain?: ChainDraft | undefined;
}

const isFile = (value: unknown): value is FormFile =>
  (typeof File !== 'undefined' && value instanceof File) ||
  (typeof value === 'object' &&
    value !== null &&
    typeof (value as PickedFile).token === 'string' &&
    typeof (value as PickedFile).name === 'string' &&
    typeof (value as PickedFile).size === 'number');

/**
 * Çekmecenin şeması: revizyon alanları ve (yeni dokümanda) doküman alanları domain şemalarıyla
 * doğrulanır (Task 2; CLAUDE.md "Form ve doğrulama"). Kapsam "seçili gemiler" ise gemi zorunlu.
 */
export function revisionFormSchema(
  mode: RevisionFormMode,
  current?: Pick<LibraryDetail, 'scope' | 'scopeVesselIds'> | null,
  variant: RevisionFormVariant = 'office',
) {
  return z
    .object({
      folderId: z.string(),
      docNo: z.string(),
      title: z.string(),
      kind: z.enum(DOCUMENT_KINDS),
      description: z.string(),
      scope: z.enum(DOCUMENT_SCOPES),
      scopeVesselIds: z.array(z.string()),
      revisionNo: revisionInputSchema.shape.revisionNo,
      changeSummary: revisionInputSchema.shape.changeSummary,
      file: z
        .custom<FormFile | null>((value) => value === null || isFile(value))
        .refine((value) => value !== null, { message: 'FILE_REQUIRED' }),
      attachments: z.array(z.custom<FormFile>(isFile)),
      printCopy: z.custom<FormFile | null>((value) => value === null || isFile(value)),
      scopeConfirmed: z.boolean(),
      approvalChain: z.custom<ChainDraft>().optional(),
    })
    .superRefine((value, ctx) => {
      // Dosya türü seçildiği anda: eski Office biçimi, makro ve desteklenmeyen tür ne yapılacağını
      // söyleyen kodla (M14b; ADR 0055 §2) [sorular #341]; sunucu denetimi tek otoritedir
      const fileProblem = value.file ? uploadFileProblem(value.file.name) : null;
      if (fileProblem) ctx.addIssue({ code: 'custom', path: ['file'], message: fileProblem });
      const attachmentProblem = value.attachments
        .map((file) => uploadFileProblem(file.name))
        .find((problem) => problem !== null);
      if (attachmentProblem) {
        ctx.addIssue({ code: 'custom', path: ['attachments'], message: attachmentProblem });
      }
      // Baskı PDF'i yalnız PDF ve yalnız Word ya da Excel ana dosyada (ADR 0028 #34(1))
      if (value.printCopy) {
        if (!isOfficeName(value.file?.name ?? '')) {
          ctx.addIssue({
            code: 'custom',
            path: ['printCopy'],
            message: 'PRINT_COPY_NOT_APPLICABLE',
          });
        } else if (!/\.pdf$/i.test(value.printCopy.name)) {
          ctx.addIssue({ code: 'custom', path: ['printCopy'], message: 'PRINT_COPY_MUST_BE_PDF' });
        }
      }
      // Gemi belgesi: kapsam yok; var olan belgede yalnız revizyon alanları (ADR 0038 §12)
      if (variant === 'ship') {
        if (mode === 'revision') return;
        const result = vesselDocumentInputSchema.safeParse({
          folderId: value.folderId,
          docNo: value.docNo,
          title: value.title,
          kind: value.kind,
          description: value.description.trim() === '' ? null : value.description,
        });
        if (result.success) return;
        for (const issue of result.error.issues) {
          ctx.addIssue({
            code: 'custom',
            path: [String(issue.path[0] ?? '')],
            message: issueCode(issue),
          });
        }
        return;
      }
      // Formun onay zinciri (ADR 0071 §2): sıra, sayılar ve rütbe tekrarı
      const chainProblem =
        value.kind === 'form' && value.approvalChain
          ? chainDraftProblem(value.approvalChain)
          : null;
      if (chainProblem) {
        ctx.addIssue({ code: 'custom', path: ['approvalChain'], message: chainProblem });
      }
      // Kapsam dokümanındır: Edit'te değişirse yürürlükteki revizyonun dağıtımı kayıtta hemen
      // değişir; kişi bunu açıkça kabul eder (inceleme M8a3-R2; ADR 0028 #28) [ISM 11.2]
      if (mode === 'revision' && current && scopeChanged(current, value) && !value.scopeConfirmed) {
        ctx.addIssue({
          code: 'custom',
          path: ['scopeConfirmed'],
          message: 'SCOPE_CHANGE_UNCONFIRMED',
        });
      }
      const result = documentInputSchema.safeParse({
        folderId: value.folderId,
        docNo: value.docNo,
        title: value.title,
        kind: value.kind,
        description: value.description.trim() === '' ? null : value.description,
        scope: value.scope,
        scopeVesselIds: value.scopeVesselIds,
      });
      if (result.success) return;
      for (const issue of result.error.issues) {
        const field = String(issue.path[0] ?? '');
        // Var olan dokümanda yalnız kapsam düzenlenir
        if (mode === 'revision' && field !== 'scopeVesselIds') continue;
        ctx.addIssue({ code: 'custom', path: [field], message: issueCode(issue) });
      }
    });
}

/** Zod sorununun arayüz kodu (`documents.validation.<KOD>`). */
function issueCode(issue: { code: string; message?: string | undefined }): string {
  if (issue.code === 'too_small') return 'REQUIRED';
  if (issue.code === 'too_big') return 'TOO_LONG';
  if (issue.code === 'custom' && issue.message && /^[A-Z_]+$/.test(issue.message)) {
    return issue.message;
  }
  return 'INVALID';
}

/**
 * Ağacın klasörleri sırayla ve yoluyla ("01. SMS › 02. Audits"). Emekli klasöre ve gösterim
 * grubuna yeni belge açılmaz (`FOLDER_RETIRED`; ADR 0038 §9).
 */
function folderOptions(all: readonly LibraryFolder[]) {
  const folders = writableFolders(all);
  const result: { value: string; label: string }[] = [];
  const visit = (parentId: string | null, prefix: string) => {
    for (const folder of folders
      .filter((f) => f.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'en'))) {
      const label = `${prefix}${folderLabel(folder)}`;
      result.push({ value: folder.id, label });
      visit(folder.id, `${label} › `);
    }
  };
  visit(null, '');
  return result;
}

/**
 * Ofiste seçilebilen ilk revizyon numarası (ADR 0107 §3): yeni dokümanda 0 (Original), yeni
 * revizyonda en büyük sayısal revizyondan sonraki; dokümanın numarası serbest metinse `null`.
 */
function revisionStart(mode: RevisionFormMode, detail: LibraryDetail | null): string | null {
  if (mode === 'new') return '0';
  return nextRevisionNo((detail?.revisions ?? []).map((revision) => revision.revisionNo));
}

function defaults(
  mode: RevisionFormMode,
  section: LibrarySection,
  folders: readonly LibraryFolder[],
  detail: LibraryDetail | null,
  folderId: string | null | undefined,
  variant: RevisionFormVariant = 'office',
): RevisionFormValues {
  const kinds = KINDS_BY_SECTION[section];
  // Yeni belge seçili belgenin klasörüyle açılır; o klasör yazılabilir değilse (emekli ya da
  // gemide şirket belgesinin yeri) ilk canlı klasör
  const options = folderOptions(folders);
  const preferred = folderId ?? detail?.folderId ?? null;
  const writable = preferred && options.some((o) => o.value === preferred) ? preferred : null;
  return {
    folderId:
      mode === 'revision' ? (detail?.folderId ?? '') : (writable ?? options[0]?.value ?? ''),
    docNo: '',
    title: '',
    kind: (section === 'ism' ? 'form' : kinds[kinds.length - 1]) ?? 'form',
    description: '',
    scope: mode === 'revision' ? (detail?.scope ?? 'fleet') : 'fleet',
    scopeVesselIds: mode === 'revision' ? [...(detail?.scopeVesselIds ?? [])] : [],
    // Ofiste revizyon seçilir (ADR 0107 §3): yeni form "Original (Rev. 0)", yeni revizyon en büyük
    // sayısal revizyondan sonraki; serbest metin numaralı dokümanda boş (kullanıcı yazar)
    revisionNo: variant === 'office' ? (revisionStart(mode, detail) ?? '') : '',
    changeSummary: '',
    file: null,
    attachments: [],
    printCopy: null,
    scopeConfirmed: false,
    approvalChain: chainToDraft(mode === 'revision' ? (detail?.approvalChain ?? null) : null, []),
  };
}

/**
 * Dosya bırakma alanı: tıklanınca dosya seçer, sürükle bırak kabul eder. Asıl denetim gizli
 * (ama odaklanabilir) dosya girdisidir; klavyeyle seçilir.
 */
function FileDrop({
  label,
  hint,
  multiple,
  files,
  error,
  disabled = false,
  optional = false,
  lockedKeys = [],
  onFiles,
  onRemove,
  onChoose,
}: {
  label: string;
  hint: string;
  multiple: boolean;
  files: readonly FormFile[];
  error?: string | undefined;
  /** Önceki denemede revizyonla yazıldı: değiştirilemez (inceleme WF-3). */
  disabled?: boolean;
  /** Tek dosyalı ama isteğe bağlı alan (baskı PDF'i; ADR 0055 §5). */
  optional?: boolean;
  /** Revizyona yazılmış ekler (`fileKey`): kaldırılamaz (inceleme M8a3-R9). */
  lockedKeys?: readonly string[] | undefined;
  onFiles: (files: FormFile[]) => void;
  onRemove: (index: number) => void;
  /**
   * Gemide dosyayı ana süreç seçer (seçme penceresi): bırakma alanı yerine düğme. Yol arayüze
   * gelmez (ADR 0038 §13).
   */
  onChoose?: (() => Promise<FormFile[]>) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.drawer.${key}`, options ?? {});
  const drop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    const list = [...event.dataTransfer.files];
    if (list.length > 0) onFiles(multiple ? list : list.slice(0, 1));
  };
  return (
    <div className={classes.drop} data-invalid={error ? true : undefined}>
      <Text component="span" size="sm" fw={500} id={`${id}-label`}>
        {label}
        {multiple || optional ? null : (
          <span className={classes.required} aria-hidden>
            {' *'}
          </span>
        )}
      </Text>
      <Text size="xs" c="dimmed" id={`${id}-hint`}>
        {hint}
      </Text>
      {onChoose ? (
        <Button
          variant="default"
          className={classes.dropChooser}
          leftSection={multiple ? <IconPaperclip size={16} /> : <IconFileUpload size={16} />}
          disabled={disabled}
          aria-labelledby={`${id}-label ${id}-button`}
          aria-describedby={`${id}-hint`}
          aria-invalid={error ? true : undefined}
          onClick={() => {
            void onChoose().then((list) => {
              if (list.length > 0) onFiles(multiple ? list : list.slice(0, 1));
            });
          }}
        >
          <span id={`${id}-button`}>{multiple ? k('addAttachments') : k('choose')}</span>
        </Button>
      ) : (
        <label
          htmlFor={`${id}-input`}
          className={classes.dropZone}
          data-over={over || undefined}
          data-disabled={disabled || undefined}
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={drop}
        >
          {multiple ? (
            <IconPaperclip size={20} aria-hidden />
          ) : (
            <IconFileUpload size={22} aria-hidden />
          )}
          <span>
            {k('drop')}{' '}
            <span className={classes.dropChoose}>
              {multiple ? k('addAttachments') : k('choose')}
            </span>
          </span>
          <input
            ref={input}
            id={`${id}-input`}
            type="file"
            multiple={multiple}
            accept={UPLOAD_ACCEPT}
            className={classes.srOnly}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-hint`}
            required={!multiple && !optional}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            onChange={(event) => {
              const list = [...(event.currentTarget.files ?? [])];
              if (list.length > 0) onFiles(list);
              event.currentTarget.value = '';
            }}
          />
        </label>
      )}
      {files.length > 0 ? (
        <ul className={classes.dropFiles}>
          {files.map((file, index) => {
            const hours = largeFileHours(file.size);
            const attached = lockedKeys.includes(fileKey(file));
            return (
              <li key={fileKey(file)}>
                <span className={classes.dropRow}>
                  <span className={classes.dropName} title={file.name}>
                    {file.name}
                  </span>
                  <span className={classes.muted}>{formatFileSize(file.size, i18n.language)}</span>
                  {attached ? <span className={classes.muted}>{k('attached')}</span> : null}
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    disabled={disabled || attached}
                    aria-label={k('remove', { name: file.name })}
                    onClick={() => onRemove(index)}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </span>
                {/* Büyük dosya uyarısı (ADR 0028 EK madde 3) */}
                {hours !== null ? (
                  <Text size="xs" className={classes.dropWarning}>
                    {k('large', {
                      size: formatFileSize(file.size, i18n.language),
                      hours: new Intl.NumberFormat(i18n.language, {
                        maximumFractionDigits: 1,
                      }).format(hours),
                    })}
                  </Text>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {error ? (
        <Text size="xs" c="red" role="alert">
          {error}
        </Text>
      ) : null}
    </div>
  );
}

export interface RevisionFormProps {
  mode: RevisionFormMode;
  section: LibrarySection;
  folders: readonly LibraryFolder[];
  /** Edit'te dokümanın kendisi; yeni dokümanda seçili doküman (klasörü varsayılan). */
  detail: LibraryDetail | null;
  vessels: readonly DocumentVessel[];
  /** Yükleme ilerlemesi; kayıt sürmüyorsa `null`. */
  progress: { label: string; sent: number; total: number } | null;
  /** Sunucu hata kodu. */
  error: string | null;
  /** Önceki denemede yazılmış, artık değiştirilemeyen alanlar (inceleme WF-3). */
  locked?: readonly (keyof RevisionFormValues)[] | undefined;
  /** Revizyona yazılmış eklerin anahtarları (inceleme M8a3-R9). */
  lockedFiles?: readonly string[] | undefined;
  defaultFolderId?: string | null | undefined;
  /** Gemide geminin kendi belgesi: kapsam yok, dosyayı ana süreç seçer (M8a4e). */
  variant?: RevisionFormVariant | undefined;
  /** Gemide dosya seçme penceresi (ana süreç). */
  onChoose?: ((multiple: boolean) => Promise<FormFile[]>) | undefined;
  /** Ofiste zincir editörünün rütbe ve rol listesi (ADR 0071 §2). */
  chainRoles?: readonly ChainRole[] | undefined;
  onSubmit: (values: RevisionFormValues) => Promise<void>;
  onCancel: () => void;
}

/**
 * Yeni doküman ya da yeni revizyon formu (plan M8a Task 17): Revision No ve Change Summary
 * zorunlu, ana dosya ve ekler (sürükle bırak), kapsam (Fleet / Selected vessels / Office).
 * Varyant seçeneği görünmez (M8c). React Hook Form ve Zod; şemalar domain'den.
 */
export function RevisionForm({
  mode,
  section,
  folders,
  detail,
  vessels,
  progress,
  error,
  locked = [],
  lockedFiles = [],
  defaultFolderId,
  variant = 'office',
  onChoose,
  chainRoles = [],
  onSubmit,
  onCancel,
}: RevisionFormProps) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.drawer.${key}`, options ?? {});
  const ship = variant === 'ship';
  const form = useForm<RevisionFormValues>({
    resolver: zodResolver(revisionFormSchema(mode, detail, variant), {
      error: (issue) => ({ message: issueCode(issue) }),
    }),
    defaultValues: defaults(mode, section, folders, detail, defaultFolderId, variant),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(isDirty && !isSubmitting);
  const fieldError = (key: keyof RevisionFormValues) => {
    const message = errors[key]?.message;
    return message
      ? t(`documents.validation.${message}`, { defaultValue: t('documents.validation.INVALID') })
      : undefined;
  };
  const scope = form.watch('scope');
  const scopeVesselIds = form.watch('scopeVesselIds');
  const scopeChange =
    !ship &&
    mode === 'revision' &&
    detail !== null &&
    scopeChanged(detail, { scope, scopeVesselIds });
  const busy = isSubmitting || progress !== null;
  const isLocked = (field: keyof RevisionFormValues) => locked.includes(field);
  // Baskı PDF'i alanı yalnız Word ya da Excel ana dosyada (ADR 0055 §5)
  const mainFile = form.watch('file');
  const printCopyField = isOfficeName(mainFile?.name ?? '') || form.watch('printCopy') !== null;
  const percent = progress && progress.total > 0 ? (progress.sent / progress.total) * 100 : 0;
  const kind = form.watch('kind');
  // Ofiste revizyon listeden seçilir; serbest metin numaralı dokümanda ve gemide yazılır (ADR 0107 §3)
  const pickFrom = ship ? null : revisionStart(mode, detail);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} noValidate className={classes.drawerForm}>
      <Stack gap="md">
        {mode === 'revision' && detail ? (
          <Text size="sm" fw={600} className={classes.drawerDocument}>
            {documentHeading(detail)}
          </Text>
        ) : null}
        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
            {t(`documents.errors.${error}`, { defaultValue: t('documents.errors.GENERIC') })}
            {error === 'NETWORK' ? <> {k('resume')}</> : null}
          </Alert>
        ) : null}
        {locked.length > 0 ? (
          <Alert color="info" icon={<IconLock size={16} />}>
            {k('locked')}
          </Alert>
        ) : null}
        {/* Gemide ISM'de yeni form ekleme doldurma değildir (ortağın geri bildirimi 2026-10-16) */}
        {ship && mode === 'new' && section === 'ism' ? (
          <Alert color="info" icon={<IconInfoCircle size={16} />} role="note" data-fill-hint>
            {k('fillHint')}
          </Alert>
        ) : null}
        {mode === 'new' ? (
          <>
            <Controller
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <Select
                  label={k('category')}
                  data={folderOptions(folders)}
                  required
                  // Seçili kategoriye yeniden tıklamak onu boşaltmaz (zorunlu alan; M14b keşfi)
                  allowDeselect={false}
                  searchable
                  value={field.value || null}
                  onChange={(value) => field.onChange(value ?? '')}
                  error={fieldError('folderId')}
                />
              )}
            />
            {/* Telefonda tek sütun (README "Responsive kurallar"; inceleme M8a3-R28) */}
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
              <TextInput
                label={k('docNo')}
                required
                maxLength={32}
                {...form.register('docNo')}
                error={fieldError('docNo')}
              />
              <Controller
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <Select
                    label={k('kind')}
                    data={KINDS_BY_SECTION[section].map((kind) => ({
                      value: kind,
                      label: t(`documents.info.kinds.${kind}`),
                    }))}
                    required
                    allowDeselect={false}
                    value={field.value}
                    onChange={(value) => field.onChange(value ?? field.value)}
                    error={fieldError('kind')}
                  />
                )}
              />
            </SimpleGrid>
            <TextInput
              label={k('title')}
              required
              maxLength={200}
              {...form.register('title')}
              error={fieldError('title')}
            />
            <Textarea
              label={k('description')}
              autosize
              minRows={2}
              maxLength={2000}
              {...form.register('description')}
              error={fieldError('description')}
            />
          </>
        ) : null}
        {pickFrom !== null ? (
          <Controller
            control={form.control}
            name="revisionNo"
            render={({ field }) => (
              <Select
                label={k('revisionNo')}
                description={k('revisionPickHint')}
                required
                allowDeselect={false}
                data={revisionChoices(Number(pickFrom), (key, values) => t(key, values ?? {}))}
                value={field.value}
                onChange={(value) => field.onChange(value ?? '')}
                onBlur={field.onBlur}
                readOnly={isLocked('revisionNo')}
                error={fieldError('revisionNo')}
              />
            )}
          />
        ) : (
          <TextInput
            label={k('revisionNo')}
            description={k('revisionNoHint')}
            required
            maxLength={32}
            readOnly={isLocked('revisionNo')}
            {...form.register('revisionNo')}
            error={fieldError('revisionNo')}
          />
        )}
        <Textarea
          label={k('changeSummary')}
          description={k('changeSummaryHint')}
          required
          autosize
          minRows={3}
          maxLength={2000}
          readOnly={isLocked('changeSummary')}
          {...form.register('changeSummary')}
          error={fieldError('changeSummary')}
        />
        <Controller
          control={form.control}
          name="file"
          render={({ field }) => (
            <FileDrop
              label={k('file')}
              hint={ship ? t('documents.library.drawer.fileHint') : k('fileHint')}
              multiple={false}
              files={field.value ? [field.value] : []}
              error={fieldError('file')}
              disabled={isLocked('file')}
              onFiles={(files) => {
                field.onChange(files[0] ?? null);
                // Tür seçildiği anda denetlenir (ADR 0055 §2)
                void form.trigger('file');
              }}
              onRemove={() => field.onChange(null)}
              onChoose={onChoose ? () => onChoose(false) : undefined}
            />
          )}
        />
        {printCopyField ? (
          <Controller
            control={form.control}
            name="printCopy"
            render={({ field }) => (
              <FileDrop
                label={k('printCopy')}
                hint={k('printCopyHint')}
                multiple={false}
                optional
                files={field.value ? [field.value] : []}
                error={fieldError('printCopy')}
                disabled={isLocked('printCopy')}
                onFiles={(files) => {
                  field.onChange(files[0] ?? null);
                  void form.trigger('printCopy');
                }}
                onRemove={() => {
                  field.onChange(null);
                  void form.trigger('printCopy');
                }}
                onChoose={onChoose ? () => onChoose(false) : undefined}
              />
            )}
          />
        ) : null}
        <Controller
          control={form.control}
          name="attachments"
          render={({ field }) => (
            <FileDrop
              label={k('attachments')}
              hint={k('attachmentsHint')}
              multiple
              files={field.value}
              lockedKeys={lockedFiles}
              error={fieldError('attachments')}
              onFiles={(files) => {
                field.onChange([...field.value, ...files]);
                void form.trigger('attachments');
              }}
              onRemove={(index) => {
                field.onChange(field.value.filter((_, i) => i !== index));
                void form.trigger('attachments');
              }}
              onChoose={onChoose ? () => onChoose(true) : undefined}
            />
          )}
        />
        {ship ? null : (
          <Controller
            control={form.control}
            name="scope"
            render={({ field }) => (
              <Radio.Group
                label={k('scope')}
                description={k('scopeHint')}
                value={field.value}
                onChange={field.onChange}
              >
                <Group gap="lg" mt={6}>
                  {DOCUMENT_SCOPES.map((value) => (
                    <Radio key={value} value={value} label={t(`documents.info.scopes.${value}`)} />
                  ))}
                </Group>
              </Radio.Group>
            )}
          />
        )}
        {!ship && scope === 'vessels' ? (
          <Controller
            control={form.control}
            name="scopeVesselIds"
            render={({ field }) => (
              <MultiSelect
                label={k('vessels')}
                placeholder={k('vesselsPlaceholder')}
                data={vessels.map((v) => ({ value: v.id, label: v.name }))}
                searchable
                required
                value={field.value}
                onChange={field.onChange}
                error={fieldError('scopeVesselIds')}
              />
            )}
          />
        ) : null}
        {scopeChange ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{k('scopeChangeWarning')}</Text>
            <Controller
              control={form.control}
              name="scopeConfirmed"
              render={({ field }) => (
                <Checkbox
                  mt="sm"
                  label={k('scopeChangeConfirm')}
                  checked={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.checked)}
                  error={fieldError('scopeConfirmed')}
                />
              )}
            />
          </Alert>
        ) : null}
        {!ship && kind === 'form' ? (
          // Formun onay zinciri (ADR 0071 §2): yüklerken ve düzenlerken seçilir
          <Controller
            control={form.control}
            name="approvalChain"
            render={({ field }) => (
              <ApprovalChainEditor
                value={field.value ?? chainToDraft(null, chainRoles)}
                roles={chainRoles}
                error={errors.approvalChain?.message ?? null}
                disabled={busy}
                onChange={(next) => {
                  field.onChange(next);
                  if (errors.approvalChain) void form.trigger('approvalChain');
                }}
              />
            )}
          />
        ) : null}
        {progress ? (
          <div className={classes.drawerProgress}>
            <Text size="xs" className={classes.dropName}>
              {k('uploading', { name: progress.label })}
            </Text>
            <Progress
              value={percent}
              size="sm"
              aria-label={k('uploading', { name: progress.label })}
            />
          </div>
        ) : null}
        <Group justify="flex-end" className={classes.drawerFooter}>
          <Button variant="default" onClick={onCancel}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export interface RevisionDrawerProps {
  opened: boolean;
  mode: RevisionFormMode;
  section: LibrarySection;
  tree: LibraryTree | null;
  detail: LibraryDetail | null;
  /** Web ofiste şirket belgesi; gemide `ship` (geminin kendi belgesi). Biri verilir. */
  office?: OfficeDocumentActions | undefined;
  ship?: ShipLibraryActions | undefined;
  onClose: () => void;
  onSaved: (documentId: string, revision: LibraryRevision) => void;
  /** Bir adım yazıldıktan sonra kayıt kesildi: ekran dokümanı yeniden okur (inceleme WF-2). */
  onPartialFailure?: ((documentId: string) => void) | undefined;
}

/**
 * Yeni doküman ve Edit (yeni revizyon) çekmecesi: doküman açılır, ana dosya tus ile yüklenir,
 * taslak revizyon yazılır, ekler eklenir; Edit'te kapsam en sonda güncellenir. Bir adım
 * kesilirse kayıt yeniden denenince tamamlanan adımlar tekrarlanmaz, yükleme kaldığı yerden
 * sürer; değişen doküman alanı yazılır, revizyon alanları kilitlenir (`revision-save.ts`).
 */
export function RevisionDrawer({
  opened,
  mode,
  section,
  tree,
  detail,
  office,
  ship,
  onClose,
  onSaved,
  onPartialFailure,
}: RevisionDrawerProps) {
  const { t } = useTranslation();
  const [vessels, setVessels] = useState<DocumentVessel[]>([]);
  const [chainRoles, setChainRoles] = useState<ChainRole[]>([]);
  const [progress, setProgress] = useState<RevisionFormProps['progress']>(null);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState<(keyof RevisionFormValues)[]>([]);
  const [lockedFiles, setLockedFiles] = useState<string[]>([]);
  const done = useRef<SaveState>(createSaveState());
  // Çekmece kapanınca kayıt kesilir: yükleme durur, bekleyen adım yazılmaz (inceleme M8a3-R11)
  const saving = useRef<AbortController | null>(null);
  const close = () => {
    saving.current?.abort();
    saving.current = null;
    onClose();
  };

  useEffect(() => {
    if (!opened) return;
    done.current = createSaveState();
    setLocked([]);
    setLockedFiles([]);
    setError(null);
    setProgress(null);
    let live = true;
    // Gemi belgesinde kapsam yok: gemi listesi okunmaz
    void (office ? office.vessels() : Promise.resolve([])).then(
      (list) => live && setVessels(list),
      () => live && setVessels([]),
    );
    // Zincir editörünün rütbeleri (ADR 0071 §2); yalnız ofiste
    void (office ? office.chainRoles() : Promise.resolve([])).then(
      (list) => live && setChainRoles(list),
      () => live && setChainRoles([]),
    );
    return () => {
      live = false;
      saving.current?.abort();
    };
  }, [opened, office]);

  /** Gemide dosyayı ana süreç seçer; yol arayüze gelmez (ADR 0038 §13). */
  const choose = ship
    ? (multiple: boolean) =>
        ship.choose({
          multiple,
          title: multiple
            ? t('documents.library.drawer.attachmentsTitle')
            : t('documents.library.drawer.chooseTitle'),
        })
    : undefined;

  const submit = async (values: RevisionFormValues) => {
    setError(null);
    const state = done.current;
    const controller = new AbortController();
    saving.current = controller;
    try {
      const saved = ship
        ? await saveShipRevisionDraft(
            ship,
            mode,
            detail,
            values,
            state,
            setProgress,
            controller.signal,
          )
        : office
          ? await saveRevisionDraft(
              office,
              mode,
              detail,
              values,
              state,
              setProgress,
              controller.signal,
            )
          : null;
      if (!saved) return;
      setProgress(null);
      onSaved(saved.documentId, saved.revision);
    } catch (failure) {
      setProgress(null);
      if (controller.signal.aborted) return;
      setError(errorCode(failure));
      setLocked(lockedFields(state));
      setLockedFiles(lockedAttachments(state));
      const documentId = state.document?.id ?? detail?.id;
      if (documentId && (state.document || state.revision || state.scope || state.chain)) {
        onPartialFailure?.(documentId);
      }
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size={520}
      title={mode === 'new' ? t('documents.drawer.newTitle') : t('documents.drawer.revisionTitle')}
      closeButtonProps={{ 'aria-label': t('documents.drawer.cancel') }}
      classNames={{ body: classes.drawerBodyForm }}
    >
      {opened ? (
        <RevisionForm
          key={`${mode}-${detail?.id ?? 'new'}`}
          mode={mode}
          section={section}
          folders={tree?.folders ?? []}
          detail={detail}
          vessels={vessels}
          progress={progress}
          error={error}
          locked={locked}
          lockedFiles={lockedFiles}
          defaultFolderId={detail?.folderId ?? null}
          variant={ship ? 'ship' : 'office'}
          onChoose={choose}
          chainRoles={chainRoles}
          onSubmit={submit}
          onCancel={close}
        />
      ) : null}
    </Drawer>
  );
}
